import clsx from "clsx";
import { format, parse } from "date-fns";
import { it } from "date-fns/locale";
import React, { useEffect } from "react";
import { useAppDispatch, useAppSelector } from "../../../lib/redux/hook.ts";
import { colors } from "../../../ui/colors.ts";
import { PlusIcon } from "../../../ui/icons/plusIcon.tsx";
import { TrashIcon } from "../../../ui/icons/trashIcon.tsx";
import { Calendar } from "../../../ui/molecules/calendar/index.tsx";
import { SelectComponent } from "../../../ui/molecules/select/index.tsx";
import { SpinnerComponent } from "../../../ui/molecules/spinner/index.tsx";
import { FinancialAdvisorAvailabilityDayDTO } from "../dto.ts";
import { setAvailabilities, setSelectedDuration } from "./slice.ts";

export function Availability() {
    const appointmentsState = useAppSelector(state => state.appointments)
    const layoutState = useAppSelector(state => state.layout)
    const authState = useAppSelector(state => state.auth)
    const dispatch = useAppDispatch();

    const days = ['Lunedì', "Martedì", "Mercoledì", "Giovedì", "Venerdì", "Sabato", "Domenica"];

    const formatTime = (date: Date | null) => (date ? format(date, "HH:mm", { locale: it }) : "");
    const parseTime = (time: string) => (time ? parse(time, "HH:mm", new Date()) : null);

    useEffect(() => {
        if (authState.findMeFinancialAdvisorStatus === "successfully" && authState.findMeFinancialAdvisorResponse?.availabilities !== null)
            dispatch(setAvailabilities(authState.findMeFinancialAdvisorResponse?.availabilities))
    }, [authState.findMeFinancialAdvisorStatus, authState.findMeFinancialAdvisorResponse])

    const addTimeSlot = (day: string) => {
        const dayEntry = appointmentsState.availabilityFinancialAdvisorRequest.availabilities !== null ? appointmentsState.availabilityFinancialAdvisorRequest.availabilities.find(_day => _day.day === day) : undefined;
        let updatedAvailability: FinancialAdvisorAvailabilityDayDTO[];

        if (dayEntry) {
            updatedAvailability = appointmentsState.availabilityFinancialAdvisorRequest.availabilities.map(_day =>
                _day.day === day
                    ? { ..._day, times: [..._day.times, { startTime: "", endTime: "" }] }
                    : _day
            );
        } else {
            const avalaible = appointmentsState.availabilityFinancialAdvisorRequest.availabilities !== null ? appointmentsState.availabilityFinancialAdvisorRequest.availabilities : []
            updatedAvailability = [...avalaible, { day, times: [{ startTime: "", endTime: "" }] }];
        }

        dispatch(setAvailabilities(updatedAvailability));
    };

    const removeTimeSlot = (day: string, index: number) => {
        const updatedAvailability = appointmentsState.availabilityFinancialAdvisorRequest.availabilities.map(_day =>
            _day.day === day
                ? { ..._day, times: _day.times.filter((_, i) => i !== index) }
                : _day
        ).filter(_day => _day.times.length > 0);

        if (updatedAvailability.length === 0)
            dispatch(setAvailabilities(null));
        else
            dispatch(setAvailabilities(updatedAvailability));
    };

    const updateTimeSlot = (day: string, index: number, field: "startTime" | "endTime", value: Date | null) => {

        const formattedTime = formatTime(value);
        const updatedAvailability = appointmentsState.availabilityFinancialAdvisorRequest.availabilities.map(_day =>
            _day.day === day
                ? {
                    ..._day,
                    times: _day.times.map((slot, i) =>
                        i === index ? { ...slot, [field]: formattedTime } : slot
                    )
                }
                : _day
        );

        dispatch(setAvailabilities(updatedAvailability));
    };

    if (appointmentsState.availabilityFinancialAdvisorStatus === "loading") {
        return (
            <div className={"flex flex-grow justify-center items-center"}>
                <SpinnerComponent />
            </div>
        )
    }
    return (
        <div className={"flex flex-col gap-5"}>
            <div className="flex flex-row justify-between w-full gap-4 overflow-auto">
                <div className="flex flex-col gap-4 w-full">
                    {days.map((day) => {
                        const dayEntry = appointmentsState.availabilityFinancialAdvisorRequest.availabilities?.find(entry => entry.day === day);
                        return (
                            <div key={day} className="flex flex-col gap-3">
                                <div className={clsx("flex items-start gap-4 min-h-[45px]", {
                                    "items-center": !dayEntry || dayEntry.times.length === 0
                                })}>
                                    <div className="p-4 rounded-full h-[20px] w-[20px] flex items-center justify-center text-white" style={{ backgroundColor: layoutState.theme[700] }}>
                                        {day.charAt(0)}
                                    </div>
                                    {dayEntry && dayEntry.times.length > 0 ? (
                                        <div className="flex flex-col gap-2">
                                            {dayEntry.times.map((slot, index) => (
                                                <div key={index} className="flex items-center gap-2 w-full">
                                                    <Calendar
                                                        type="time"
                                                        label="Ora inizio"
                                                        timeIntervals={appointmentsState.availabilityFinancialAdvisorRequest.duration}
                                                        startDate={slot.startTime ? parseTime(slot.startTime) : null}
                                                        setStartDate={(date) => updateTimeSlot(day, index, "startTime", date)}
                                                    />
                                                    <Calendar
                                                        type="time"
                                                        label="Ora fine"
                                                        timeIntervals={appointmentsState.availabilityFinancialAdvisorRequest.duration}
                                                        startDate={slot.endTime ? parseTime(slot.endTime) : null}
                                                        setStartDate={(date) => updateTimeSlot(day, index, "endTime", date)}
                                                    />
                                                    <div className="flex flex-row gap-1 items-center mt-[15px]">
                                                        <PlusIcon size="20" color={colors.brandSecondary[500]} onClick={() => addTimeSlot(day)} />
                                                        <TrashIcon size="20" color={colors.brandSecondary[500]} onClick={() => removeTimeSlot(day, index)} />
                                                    </div>
                                                </div>
                                            ))}
                                        </div>
                                    ) : (
                                        <div className="flex items-center gap-2">
                                            <span className="text-neutral-500">Non disponibile</span>
                                            <PlusIcon size="24" color={colors.brandSecondary[500]} onClick={() => addTimeSlot(day)} />
                                        </div>
                                    )}
                                </div>
                            </div>
                        );
                    })}
                </div>
                <div className="flex items-start w-full">
                    <SelectComponent
                        label="Durata appuntamento"
                        placeholder={"Scegli la durata"}
                        options={[
                            { label: "15 minuti", value: "15" },
                            { label: "30 minuti", value: "30" },
                            { label: "45 minuti", value: "45" },
                            { label: "1 ora", value: "60" },
                            { label: "2 ore", value: "120" }
                        ]}
                        onChange={(e) => dispatch(setSelectedDuration(e))}
                        value={appointmentsState.availabilityFinancialAdvisorRequest.duration.toString()}
                    />
                </div>
            </div>

        </div>
    );
}


